// @flow
import React, { useState, useEffect } from 'react';
import type { Element } from 'react';

import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import noop from 'lodash/fp/noop';

import { getCityFromGooglePlacesResults, getPostcodeFromGooglePlacesResults } from '@lib/helpers';

import { withTranslation } from '@i18n';

import type { Props } from './types';
import { StyledGoogleLogo } from './styles';

export const INPUT_CONTAINER_CLASSNAME = 'location-search-input-container';
export const INPUT_CLASSNAME = 'location-search-input';
export const DROPDOWN_CLASSNAME = 'autocomplete-dropdown-container';
export const SUGGESTION_CLASSNAME = 'suggestion-item';
export const SUGGESTION_ACTIVE_CLASSNAME = 'suggestion-item-active';

const InputPlacesAutocomplete = ({
  onCityFound,
  onPostcodeFound = noop,
  initialValue = '',
  placeholder,
  icon,
  t
}: Props): Element<any> | null => {
  const [address, setAddress] = useState(initialValue);
  const [checkIntervalCount, setCheckIntervalCount] = useState(0);
  const [mapsApiLoaded, setMapsApiLoaded] = useState(false);

  useEffect(() => {
    if (window.google?.maps?.places || process.env.JEST_WORKER_ID !== undefined) return;

    const script = document.createElement('script');

    script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyA21AGEX2tPgXZ8HPDhejctehltgVZzkx8&libraries=places';
    script.async = true;

    if (document.body) document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (initialValue === '') setAddress(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (window.google?.maps?.places || process.env.JEST_WORKER_ID !== undefined) {
      setMapsApiLoaded(true);
      return noop;
    }

    const timeOut = window.setTimeout(() => {
      setCheckIntervalCount(checkIntervalCount + 1);
    }, 300);

    return () => {
      window.clearTimeout(timeOut);
    };
  }, [checkIntervalCount]);

  const handleSelect = async (address) => {
    const results: Array<{
      address_components: Array<{ types: Array<string>, long_name: string }>,
      geometry: { location: {} }
    }> = await geocodeByAddress(address).catch(noop);

    const city: string = getCityFromGooglePlacesResults(results);

    setAddress(city);
    onCityFound(city);

    const postcode: string = await getPostcodeFromGooglePlacesResults(results);

    onPostcodeFound(postcode);
  };

  const handleChange = (address) => {
    setAddress(address);
  };

  if (!mapsApiLoaded) {
    return (
      <div>
        <div className={INPUT_CONTAINER_CLASSNAME}>
          {icon || null}
          <input placeholder={placeholder} className={INPUT_CLASSNAME} tabIndex="-1" disabled />
        </div>
      </div>
    );
  }

  return (
    <PlacesAutocomplete
      value={address}
      searchOptions={{
        types: ['(regions)'],
        componentRestrictions: { country: 'DE' }
      }}
      onChange={handleChange}
      onSelect={handleSelect}
    >
      {({
        getInputProps, suggestions, getSuggestionItemProps, loading
      }) => {
        const slicedSuggestions = suggestions.slice(0, 4);
        return (
          <div>
            <div className={INPUT_CONTAINER_CLASSNAME}>
              {icon || null}
              <input
                {...getInputProps({
                  placeholder,
                  className: INPUT_CLASSNAME
                })}
                tabIndex="-1"
              />
            </div>
            <div className={`${DROPDOWN_CLASSNAME}`}>
              <StyledGoogleLogo showLogo={slicedSuggestions.length > 0}>
                {loading && <div className={SUGGESTION_CLASSNAME}>{`${t('Loading')}...`}</div>}
                {slicedSuggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? SUGGESTION_ACTIVE_CLASSNAME
                    : SUGGESTION_CLASSNAME;
                  return (
                    <div
                      key={suggestion.id}
                      {...getSuggestionItemProps(suggestion, {
                        className
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </StyledGoogleLogo>
            </div>
          </div>
        );
      }}
    </PlacesAutocomplete>
  );
};

InputPlacesAutocomplete.getInitialProps = (): {} => ({
  namespacesRequired: ['common']
});

export default withTranslation('common')(InputPlacesAutocomplete);
